import { CheckCircle2 } from 'lucide-react'
import type { JobEntry } from '@/features/ai/jobsSlice'
import { countedPhrase, declaredSkips } from './runOutcomeView'
import { SkippedList } from './SkippedList'
import { useMessages } from '@/i18n'
import { messages } from './sidebarMessages'

/**
 * Закрытый итог ПЕРВОЙ джобы цепочки (§7.4) — или родителя, чей прогон
 * доделывается продолжением (§8). Живёт над живым прогрессом следующей
 * джобы: перецепив поллинг, панель теряет `result` родителя — его числа и
 * пропуски автор ещё не видел, а взять их больше неоткуда.
 *
 * Глагол результата и разметка пропусков — ОБЩИЕ с итогом прогона
 * (`runOutcomeView` + `SkippedList`): собранные здесь руками, они разъехались
 * бы с `RunOutcome` при первой же правке копирайта.
 *
 * Повод различаем ЯВНЫМ `previous.kind`, а НЕ наличием `reason` (финальное
 * ревью, п.1): у родителя, завершившегося `success` с непустым
 * `result.skipped`, тоже штатный кандидат на продолжение (§8 — пропущенные
 * единицы возвращаются в план), а `error_message`/`error_code` у него пусты
 * — вывод по `reason` молча показал бы «Дорисовываю картинки второго
 * языка…» поверх продолжения совсем другого прогона. Тон для продолжения
 * нарочно нейтральный — карточка итога родителя уже объявила исход, второй
 * тревожный сигнал был бы шумом.
 *
 * Денежного обещания здесь больше нет (финальное ревью, п.4): §8 контракта
 * прямо перечисляет исключения из «второй раз не платят» — пропущенные
 * единицы продолжение оплачивает заново (модель на них уже отвечала), а у
 * генерации недостающие уроки НЕДОЕХАВШЕГО батча (контент пишется по 4
 * урока, отчёт о токенах — один на весь вызов) продолжение оплатит вторым
 * вызовом. Остаётся только то, что контракт держит буквально: записанное не
 * переделывается. Список пропусков честно подписан для продолжения — эти
 * единицы прогон именно ПОПРОБУЕТ ЗАНОВО, а не просто перечисляет их судьбу.
 */
export const PreviousRunNotice = ({
  previous,
}: {
  previous: NonNullable<JobEntry['previous']>
}) => {
  const m = useMessages(messages)
  const skips = declaredSkips(previous.type, previous.skipped, previous.skipped_codes)
  const isResumed = previous.kind === 'resume'
  return (
    <div className="m-3 rounded-2xl border border-success/30 bg-success/10 p-3 text-sm">
      <p className="flex items-center gap-2 text-success">
        <CheckCircle2 className="size-4" />
        {countedPhrase(previous.type, previous.progress)}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        {isResumed ? m.resumedNotice : m.translatedNotice}
      </p>
      {isResumed && previous.reason ? (
        <p className="mt-1 text-xs text-muted-foreground">{previous.reason}</p>
      ) : null}
      {skips.length ? (
        <div className="mt-2">
          {isResumed ? <p className="text-xs text-muted-foreground">{m.retryUnits}</p> : null}
          <SkippedList items={skips} />
        </div>
      ) : null}
    </div>
  )
}
